import React, { useEffect, useRef, useState } from 'react';
import { Search, FileText, Monitor, Shield, ArrowRight, Clock } from 'lucide-react';
import { journeyData } from '../../data/mock';

const iconMap = {
  Search,
  FileText,
  Monitor,
  Shield
};

const JourneySection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: 'white' }}
    >
      <div className="container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isVisible ? 'animate-fadeInUp' : 'opacity-0'
            }`}
            style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
          >
            <Clock size={16} />
            <span className="font-semibold text-sm">{journeyData.subtitle}</span>
          </div>
          
          <h2 
            className={`heading-1 mb-4 ${isVisible ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}
            style={{ color: 'var(--text-primary)' }}
          >
            {journeyData.title}
          </h2>
        </div>

        {/* Journey Timeline */}
        <div className="relative max-w-5xl mx-auto">
          {/* Connection Line - Desktop */}
          <div 
            className="hidden lg:block absolute top-32 left-0 right-0 h-1 rounded-full"
            style={{ background: 'rgba(0, 69, 52, 0.1)' }}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-4">
            {journeyData.phases.map((phase, index) => {
              const Icon = iconMap[phase.icon];
              return (
                <div 
                  key={index}
                  className={`relative ${
                    isVisible ? 'animate-fadeInUp' : 'opacity-0'
                  }`}
                  style={{ animationDelay: `${(index + 2) * 100}ms` }}
                >
                  {/* Phase Card */}
                  <div className="card p-6 text-center h-full" style={{ background: 'var(--bg-page)' }}>
                    {/* Phase Number */}
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 relative z-10"
                      style={{ background: 'var(--brand-dark)' }}
                    >
                      <span className="text-white font-bold">{index + 1}</span>
                    </div>

                    {/* Icon */}
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                      style={{ background: 'var(--brand-primary)' }}
                    >
                      <Icon size={28} style={{ color: 'var(--brand-dark)' }} />
                    </div>

                    {/* Duration Badge */}
                    <div 
                      className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3"
                      style={{ background: 'rgba(0, 69, 52, 0.1)', color: 'var(--brand-dark)' }}
                    >
                      {phase.duration}
                    </div>

                    {/* Phase Title */}
                    <h3 className="heading-3 mb-2" style={{ fontSize: '1rem', color: 'var(--text-primary)' }}>
                      {phase.title}
                    </h3>

                    {/* Description */}
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {phase.description}
                    </p>
                  </div>

                  {/* Arrow - Mobile */}
                  {index < journeyData.phases.length - 1 && (
                    <div className="lg:hidden flex justify-center my-2">
                      <ArrowRight 
                        size={24} 
                        className="rotate-90 md:rotate-0"
                        style={{ color: 'var(--brand-dark)' }} 
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Closing Statement */}
        <div className={`text-center mt-12 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '700ms' }}>
          <p 
            className="heading-3 inline-block px-6 py-3 rounded-full"
            style={{ background: 'rgba(0, 69, 52, 0.08)', color: 'var(--brand-dark)' }}
          >
            {journeyData.closing}
          </p>
        </div>
      </div>
    </section>
  );
};

export default JourneySection;
